import { useEffect, useState, useCallback } from "react";
import { Save, RotateCcw, ChevronDown, ChevronUp } from "lucide-react";
import { toast } from "sonner";
import { authFetch, apiErr } from "../../lib/adminApi";
import { SITE_CONTENT_SCHEMAS } from "../../data/siteContentSchemas";
import { DEFAULTS, applyContent } from "../../lib/siteContent";
import { AdminBar } from "./AdminCore";
import { FieldEditor } from "./EditorFields";

const API = process.env.REACT_APP_BACKEND_URL;
const KEYS = Object.keys(SITE_CONTENT_SCHEMAS);

// stored lists are wrapped as { items: [...] }; objects are stored as-is
const toEditable = (key, remote) => {
  const schema = SITE_CONTENT_SCHEMAS[key];
  const def = DEFAULTS[key];
  if (schema.list) return { items: Array.isArray(remote?.items) ? remote.items : def };
  return { ...(def || {}), ...(remote || {}) };
};

function Section({ k, value, dirty, onChange, onSave, onReset, open, toggle }) {
  const schema = SITE_CONTENT_SCHEMAS[k];
  return (
    <div className="border border-white/12" data-testid={`site-section-${k}`}>
      <div className="flex items-center justify-between px-5 py-4 cursor-pointer hover:bg-white/[0.03]" onClick={toggle} data-testid={`site-section-toggle-${k}`}>
        <div className="min-w-0">
          <p className="text-white text-sm font-medium">{schema.label}{dirty && <span className="ml-3 text-[10px] tracking-[0.2em] uppercase text-[#F2C230]">Unsaved</span>}</p>
          {schema.help && <p className="text-white/40 text-xs mt-1">{schema.help}</p>}
        </div>
        {open ? <ChevronUp size={16} className="text-white/50 shrink-0" /> : <ChevronDown size={16} className="text-white/50 shrink-0" />}
      </div>
      {open && (
        <div className="border-t border-white/10 p-6 space-y-5" data-testid={`site-section-form-${k}`}>
          {schema.list ? (
            <FieldEditor def={{ k: "items", t: "items", label: schema.label, fields: schema.fields }} prefix={`sc-${k}`} value={value.items}
              onChange={(v) => onChange({ items: v })} />
          ) : (
            schema.fields.map((f) => (
              <FieldEditor key={f.k} def={f} prefix={`sc-${k}`} value={value[f.k]} onChange={(v) => onChange({ ...value, [f.k]: v })} />
            ))
          )}
          <div className="flex flex-wrap gap-3 pt-2">
            <button onClick={onSave} data-testid={`site-save-${k}`} className="btn-solid"><Save size={14} /> Save & publish</button>
            <button onClick={onReset} data-testid={`site-reset-${k}`} className="btn-ghost"><RotateCcw size={14} /> Restore defaults</button>
          </div>
        </div>
      )}
    </div>
  );
}

export default function AdminSiteContent() {
  const [remote, setRemote] = useState(null);
  const [values, setValues] = useState({});
  const [dirty, setDirty] = useState({});
  const [open, setOpen] = useState(null);

  const load = useCallback(() => {
    fetch(`${API}/api/site-content`).then((r) => r.json()).then((d) => {
      setRemote(d);
      setValues(Object.fromEntries(KEYS.map((k) => [k, toEditable(k, d[k])])));
      setDirty({});
    }).catch(() => toast.error("Could not load site content"));
  }, []);

  useEffect(() => { document.title = "Admin · Site Content | Cedar Views"; load(); }, [load]);

  const save = async (k) => {
    const res = await authFetch(`/api/site-content/${k}`, { method: "PUT", body: JSON.stringify({ data: values[k] }) });
    const data = await res.json();
    if (!res.ok) { toast.error(apiErr(data.detail)); return; }
    toast.success(`${SITE_CONTENT_SCHEMAS[k].label} saved — changes are live`);
    setDirty((d) => ({ ...d, [k]: false }));
    applyContent({ [k]: values[k] });
    try { sessionStorage.removeItem("cv-content"); } catch {}
  };

  const reset = async (k) => {
    if (!window.confirm(`Restore the original ${SITE_CONTENT_SCHEMAS[k].label} content? Your edits to this section will be removed.`)) return;
    const res = await authFetch(`/api/site-content/${k}`, { method: "DELETE" });
    if (!res.ok) { toast.error("Could not reset"); return; }
    toast.success("Defaults restored");
    const def = JSON.parse(JSON.stringify(DEFAULTS[k]));
    applyContent({ [k]: SITE_CONTENT_SCHEMAS[k].list ? { items: def } : def });
    try { sessionStorage.removeItem("cv-content"); } catch {}
    load();
  };

  if (!remote) return <main className="pt-40 min-h-screen bg-[#050A15]" data-testid="site-content-loading" />;

  return (
    <main className="pt-40 pb-24 min-h-screen bg-[#050A15]" data-testid="admin-site-content">
      <div className="max-w-[1100px] mx-auto px-6 lg:px-10">
        <AdminBar title="Site Content." />
        <p className="text-white/50 text-sm max-w-2xl mb-10">
          Everything outside the product pages: contact details, menus, homepage, showrooms, contact page, testimonials, projects, privacy
          policy and the professionals pages. Open a section, edit, then Save & publish. Changes appear on the live site immediately.
        </p>
        <div className="space-y-3">
          {KEYS.map((k) => (
            <Section key={k} k={k} value={values[k]} dirty={!!dirty[k]} open={open === k} toggle={() => setOpen(open === k ? null : k)}
              onChange={(v) => { setValues((s) => ({ ...s, [k]: v })); setDirty((d) => ({ ...d, [k]: true })); }}
              onSave={() => save(k)} onReset={() => reset(k)} />
          ))}
        </div>
      </div>
    </main>
  );
}
